import type { Application, Domain } from '@3d4medical/websdk';
import {
  ApplicationFactory,
  LanguageType,
  TextureSize,
} from '@3d4medical/websdk';
import { useBridge } from '@completeanatomy/bridge';
import { useConfigService } from '@completeanatomy/config';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { globalVariables } from '../config/globalVariables';

import { useAuthentication } from './useAuthentication';
import useCanvasAccessibility from './useCanvasAccessibility';

const isIosDevice = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();

  return /iphone|ipad/.test(userAgent); // Only iPhone and iPad
};

const defaultDeeplink = 'cDX1Vkbi';

// eslint-disable-next-line max-lines-per-function -- @todo refactor this hook into smaller hooks for better readability and testability
const useApp = () => {
  const [app, setApp] = useState<Application | null>(null);
  const bridge = useBridge();
  const config = useConfigService();
  const isCreatingApp = useRef(false);
  const fileCounter = useRef(0);
  const fileCounterLastBunch = useRef(0);
  const timeoutId = useRef<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { i18n } = useTranslation();
  const { setupAuthentication, checkUserAuthentication } = useAuthentication();

  useEffect(() => {
    /**
     * Show loading state when app is being created
     */
    if (isCreatingApp.current) {
      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: true,
        },
      });
    } else {
      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: false,
        },
      });
    }
  }, [isCreatingApp, bridge]);

  const createApp = useCallback(async () => {
    const appId = globalVariables.appId;

    if (!appId) {
      console.error('No application id provided');
      isCreatingApp.current = false;

      return;
    }

    let language = i18n.language as LanguageType;
    // TODO: This approach won't work with chinese locale

    if (!Object.values(LanguageType).includes(language)) {
      await i18n.changeLanguage('en');
      language = LanguageType.English;
    }

    const match = location.pathname.match(/\/screens\/([^/]+)\/?/);
    const deeplink = match ? match[1] : defaultDeeplink;

    config?.set(
      'OPEN_IN_CA_LINK',
      `${globalVariables.caDeeplinkDomain}/?deeplink=${deeplink}`
    );

    const factory = new ApplicationFactory(
      (process.env.REACT_APP_KC_TOKEN
        ? process.env.KONG_API_DOMAIN
        : '') as Domain
    );
    const token = await setupAuthentication();

    console.log('creating app with ID:', appId);
    const application = await factory.createApplication(
      appId,
      token || '',
      'model-canvas'
    );

    const textureSize = isIosDevice()
      ? TextureSize.LowGradual
      : TextureSize.MediumGradual;

    application.settings = {
      ...application.settings,
      texture: {
        size: textureSize,
      },
      language: language,
      disableToolkit: true,
      colors: {
        ...application.settings.colors,
        background: {
          r: 17 / 255,
          g: 21 / 255,
          b: 28 / 255,
        },
      },
    };

    setApp(application);
    isCreatingApp.current = false;
  }, [setupAuthentication, config, i18n, location.pathname]);

  const setupOnboarding = useCallback(async () => {
    await setupAuthentication();

    // No app creation for onboarding - just authentication setup
    setApp(null);
    isCreatingApp.current = false;
  }, [setupAuthentication]);

  const setup = useCallback(async () => {
    const isScreenPage =
      location.pathname.includes('/screens/') ||
      location.pathname.includes('/models/');

    config?.set(
      'OPEN_IN_CA_LINK',
      `${globalVariables.caDeeplinkDomain}/?deeplink=${defaultDeeplink}`
    );

    const isAuthenticated = await checkUserAuthentication();

    if (!isAuthenticated) {
      return;
    }

    if (isScreenPage) {
      try {
        await createApp();
      } catch (error) {
        console.error('Error during app creation:', error);
      }
    } else {
      await setupOnboarding();
    }
  }, [
    setupOnboarding,
    createApp,
    checkUserAuthentication,
    location.pathname,
    config,
  ]);

  // set canvas when app is existed
  useEffect(() => {
    const canvas = document.getElementById('model-canvas') as HTMLCanvasElement;
    const canvasContainer = document.getElementById(
      'model-container'
    ) as HTMLElement;

    if (!app) {
      canvasContainer.style.backgroundColor = '#11151C';
      canvas.style.backgroundColor = '#11151C';

      return;
    }

    if (!canvas || !canvasContainer) {
      console.warn('Required canvas elements not found in DOM');

      return;
    }

    // Remove unused canvas from websdk
    document.getElementById('canvas')?.remove();

    if (
      canvasContainer.clientHeight !== 0 &&
      canvasContainer.clientWidth !== 0
    ) {
      app.canvas.setSize(
        canvasContainer.clientWidth,
        canvasContainer.clientHeight
      );
    }

    // Resize the canvas when the container size changes
    const observer = new ResizeObserver(() => {
      if (!app || !app.canvas) {
        observer.disconnect();

        return;
      }

      if (
        canvasContainer.clientHeight !== 0 &&
        canvasContainer.clientWidth !== 0
      ) {
        app.canvas.setSize(
          canvasContainer.clientWidth,
          canvasContainer.clientHeight
        );
      }
    });

    observer.observe(canvasContainer);
    window.dispatchEvent(new Event('resize'));
  }, [app]);

  useCanvasAccessibility('model-canvas');

  useEffect(() => {
    const handleOffline = () => {
      navigate('/download-error');
    };

    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('offline', handleOffline);
    };
  }, [navigate]);

  useEffect(() => {
    if (isCreatingApp.current || app) {
      return;
    }

    isCreatingApp.current = true;

    setup();
  }, [app, bridge, config, setup]);

  useEffect(() => {
    if (!app) {
      return;
    }

    app.events.onFailedDownload = () => {
      navigate('/download-error');
    };

    app.events.onStartedDownload = () => {
      if (timeoutId.current) {
        window.clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }

      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: true,
        },
      });
    };

    app.events.onCurrentStateDownload = (current, maxSize) => {
      const progress =
        ((current + fileCounterLastBunch.current - 1) /
          (maxSize + fileCounterLastBunch.current)) *
        100;

      fileCounter.current += 1;

      bridge?.emit('BridgeObjectChanged', {
        identifier: 'modelState',
        keyPath: '',
        changes: {
          isLoading: true,
          downloadingProgress: progress,
        },
      });
    };

    app.events.onFinishedDownload = () => {
      fileCounterLastBunch.current += fileCounter.current;
      fileCounter.current = 0;

      timeoutId.current = window.setTimeout(() => {
        bridge?.emit('BridgeObjectChanged', {
          identifier: 'modelState',
          keyPath: '',
          changes: {
            isLoading: false,
            downloadingProgress: 0,
          },
        });

        timeoutId.current = null;
        fileCounterLastBunch.current = 0;
        fileCounter.current = 0;
      }, 500);
    };
  }, [app, bridge, navigate]);

  const deactivateApp = async (): Promise<void> => {
    console.log('Deactivating app', app);

    if (!app) {
      return;
    }

    await app.delete();
    setApp(null);
  };

  return { app, deactivateApp };
};

export default useApp;
